// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchTeacherSubjectsWithStudents,
  fetchAdminSubjectsWithStudents,
  bulkDownloadFamilyReports,
  fetchStudentProgressPdf,
} from '../services/api';
import StudentNameOrderToggle from '../components/StudentNameOrderToggle';
import StudentTranscript from '../components/StudentTranscript';
import { sortStudentsByName, useStudentNameOrder } from '../utils/studentSort';
import { CORE_GRADE_ORDER } from '../utils/progressStatus';

const S = {
  page: { padding: '0 0 48px' },
  title: { fontSize: 28, fontWeight: 800, color: 'var(--ae-primary-strong)', marginBottom: 6 },
  subtitle: { fontSize: 14, color: 'var(--ae-text-muted)', marginBottom: 28 },
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 16, fontWeight: 800, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.04em' },
  card: {
    background: '#fff',
    border: '1px solid var(--ae-border)',
    borderRadius: 14,
    padding: '18px 20px',
    boxShadow: 'var(--ae-shadow-card)',
    marginBottom: 14,
  },
  btnRow: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  btnPrimary: {
    background: 'var(--ae-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
  btnSecondary: {
    background: '#f0f4ff',
    color: 'var(--ae-primary-strong)',
    border: '1px solid var(--ae-border)',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
  btnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  msg: { marginTop: 10, fontSize: 13, fontWeight: 600 },
  msgOk: { color: '#166534' },
  msgErr: { color: '#b91c1c' },
  studentList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', marginBottom: 14 },
  studentRow: (checked) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '9px 12px',
    borderRadius: 8,
    border: checked ? '1px solid #16a34a' : '1px solid #e5e7eb',
    background: checked ? '#f0fdf4' : '#f9fafb',
    cursor: 'pointer',
  }),
  selectAllRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    paddingBottom: 10,
    borderBottom: '1px solid #e5e7eb',
  },
};

function studentLabel(student) {
  return student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`;
}

function FamilyReports({ classCards }: any) {
  const [filters, setFilters] = useState({ student: '', class: '', grade: '', subject: '' });
  const [selected, setSelected] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState<any>(false);
  const [singleDownloading, setSingleDownloading] = useState<any>(null); // student_id
  const [msg, setMsg] = useState<any>('');
  const [downloadError, setDownloadError] = useState<any>(false);
  const [fromDate, setFromDate] = useState<any>('');
  const [throughDate, setThroughDate] = useState<any>('');
  const invalidDates = Boolean(fromDate && throughDate && fromDate > throughDate);
  const dateOptions = fromDate || throughDate ? [{ fromDate, throughDate }] : [];
  const [nameOrder] = useStudentNameOrder();

  const enrollments = useMemo(() => classCards.flatMap(({ cls, subjectName }) =>
    (cls.students || []).map(student => ({
      student: String(student.student_id),
      class: String(cls.class_id),
      grade: String(student.grade_level ?? '').trim().toUpperCase(),
      subject: subjectName,
      className: cls.class_name || `Class ${cls.class_id}`,
      record: student,
    }))
  ), [classCards]);
  const matches = (entry, excluded) => Object.entries(filters).every(([key, value]) =>
    key === excluded || !value || entry[key] === value);
  const options = Object.fromEntries(['student', 'class', 'grade', 'subject'].map(key => {
    const entries = enrollments.filter(entry => matches(entry, key));
    const unique = new Map(entries.filter(entry => entry[key]).map(entry => [
      entry[key], {
        value: entry[key],
        label: key === 'student' ? studentLabel(entry.record)
          : key === 'class' ? entry.className : entry[key],
        record: entry.record,
      },
    ]));
    const values = [...unique.values()];
    if (key === 'student') {
      const ordered = sortStudentsByName(values.map(option => option.record), nameOrder);
      return [key, ordered.map(student => unique.get(String(student.student_id)))];
    }
    values.sort((a, b) => key === 'grade'
      ? (CORE_GRADE_ORDER.indexOf(a.value) === -1 ? 99 : CORE_GRADE_ORDER.indexOf(a.value))
        - (CORE_GRADE_ORDER.indexOf(b.value) === -1 ? 99 : CORE_GRADE_ORDER.indexOf(b.value))
        || a.label.localeCompare(b.label)
      : a.label.localeCompare(b.label));
    return [key, values];
  }));
  const matchingStudents = [...new Map(enrollments.filter(entry => matches(entry))
    .map(entry => [entry.student, entry.record])).values()];
  const students = sortStudentsByName(matchingStudents, nameOrder);
  const matchingIds = new Set(students.map(student => String(student.student_id)));
  const selectedIds = [...selected].filter(id => matchingIds.has(String(id)));
  const selectedClassId = filters.class ? Number(filters.class) : undefined;

  const allSelected = students.length > 0 && selectedIds.length === students.length;

  const updateFilters = next => {
    setFilters(next);
    setSelected(new Set());
    setMsg('');
  };

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(students.map((s) => s.student_id)));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkDownload = async ids => {
    if (invalidDates) return;
    if (!ids.length || ids.length > 100) {
      setDownloadError(true);
      setMsg('Select between 1 and 100 students per download.');
      return;
    }
    setBulkDownloading(true);
    setMsg('');
    setDownloadError(false);
    try {
      const blob = await bulkDownloadFamilyReports(ids, selectedClassId, ...dateOptions);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'family_reports.zip';
      a.click();
      URL.revokeObjectURL(url);
      setMsg(`Downloaded ${ids.length} family report${ids.length !== 1 ? 's' : ''}.`);
    } catch {
      setDownloadError(true);
      setMsg('Download failed. Please try again.');
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleSingleDownload = async (student) => {
    if (invalidDates) return;
    setSingleDownloading(student.student_id);
    setMsg('');
    setDownloadError(false);
    try {
      const blob = await fetchStudentProgressPdf(student.student_id, selectedClassId, ...dateOptions);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const name = `${student.first_name || ''}_${student.last_name || ''}`.trim().replace(/\s+/g, '_') || `student_${student.student_id}`;
      a.href = url;
      a.download = `${name}_report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(true);
      setMsg('Could not generate report. Please try again.');
    } finally {
      setSingleDownloading(null);
    }
  };

  const isBusy = bulkDownloading || singleDownloading != null;

  return (
    <section aria-label="Family reports" style={{ ...S.section, ...S.card }}>
      <h2 style={S.sectionTitle}>Find students</h2>
      <fieldset disabled={isBusy} style={{ border: 0, padding: 0, margin: '0 0 18px' }}>
        <legend style={{ fontSize: 13, marginBottom: 10 }}>Shortlist students for family reports</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
          {['student', 'class', 'grade', 'subject'].map(key => (
            <label key={key} style={{ display: 'grid', gap: 6, fontWeight: 700, fontSize: 13 }}>
              {key[0].toUpperCase() + key.slice(1)}
              <select value={filters[key]} onChange={event => updateFilters({ ...filters, [key]: event.target.value })}
                style={{ width: '100%', minHeight: 40, border: '1px solid var(--ae-border)', borderRadius: 8, background: '#fff', padding: '8px 10px' }}>
                <option value="">All {key === 'class' ? 'classes' : `${key}s`}</option>
                {options[key].map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ))}
          <label style={{ display: 'grid', gap: 6, fontWeight: 700, fontSize: 13 }}>
            From
            <input type="date" value={fromDate} max={throughDate || undefined}
              onChange={event => { setFromDate(event.target.value); setMsg(''); }}
              style={{ minHeight: 40, border: '1px solid var(--ae-border)', borderRadius: 8, padding: '8px 10px' }} />
          </label>
          <label style={{ display: 'grid', gap: 6, fontWeight: 700, fontSize: 13 }}>
            Through
            <input type="date" value={throughDate} min={fromDate || undefined}
              onChange={event => { setThroughDate(event.target.value); setMsg(''); }}
              style={{ minHeight: 40, border: '1px solid var(--ae-border)', borderRadius: 8, padding: '8px 10px' }} />
          </label>
        </div>
        <p style={{ fontSize: 13 }}>Leave dates blank for all history. Both dates are inclusive; report colors use assessments recorded within the selected period.</p>
        {invalidDates && <p role="alert">From must be on or before Through.</p>}
        <button type="button" style={{ ...S.btnSecondary, marginTop: 12 }}
          onClick={() => {
            updateFilters({ student: '', class: '', grade: '', subject: '' });
            setFromDate('');
            setThroughDate('');
          }}>Clear filters</button>
      </fieldset>
      <div style={{ ...S.btnRow, alignItems: 'center', marginBottom: 14 }}>
        <span role="status">{students.length} matching student{students.length !== 1 ? 's' : ''}</span>
        <button
          type="button" style={{ ...S.btnPrimary, ...(!students.length || students.length > 100 || isBusy ? S.btnDisabled : {}) }}
          disabled={!students.length || students.length > 100 || isBusy || invalidDates}
          onClick={() => handleBulkDownload(students.map(student => student.student_id))}
        >
          {bulkDownloading ? 'Generating…' : `Generate Family Reports (${students.length})`}
        </button>
      </div>
      {students.length > 100 && <p>Up to 100 students can be downloaded at once. Narrow the filters or select up to 100 students below.</p>}
      {students.length === 0 ? (
        <p>No students match these filters. Clear the filters to try again.</p>
      ) : (
        <>
          <div style={S.selectAllRow}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#16a34a' }}
                disabled={isBusy}
              />
              Select All
            </label>
            <StudentNameOrderToggle />
            <button
              type="button"
              style={{ ...S.btnPrimary, ...(selectedIds.length === 0 || selectedIds.length > 100 || isBusy ? S.btnDisabled : {}) }}
              disabled={selectedIds.length === 0 || selectedIds.length > 100 || isBusy || invalidDates}
              onClick={() => handleBulkDownload(selectedIds)}
            >
              {bulkDownloading ? 'Generating…' : `Download Selected (${selectedIds.length})`}
            </button>
          </div>
          <div style={S.studentList}>
            {students.map((student) => {
              const name = studentLabel(student);
              const checked = selected.has(student.student_id);
              const isGenerating = singleDownloading === student.student_id;
              return (
                <div key={student.student_id} style={S.studentRow(checked)}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${name}`}
                    checked={checked}
                    onChange={() => toggleOne(student.student_id)}
                    style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#16a34a', flexShrink: 0 }}
                    disabled={isBusy || invalidDates}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1e3a5f' }}>{name}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Grade {student.grade_level ?? 'N/A'}</div>
                  </div>
                  <button
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#2563eb',
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: isBusy ? 'not-allowed' : 'pointer',
                      opacity: isBusy ? 0.5 : 1,
                      padding: '4px 8px',
                      flexShrink: 0,
                    }}
                    disabled={isBusy || invalidDates}
                    onClick={() => handleSingleDownload(student)}
                  >
                    {isGenerating ? 'Generating…' : 'Generate Family Report'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
      {msg && (
        <div role={downloadError ? 'alert' : 'status'} style={{ ...S.msg, ...(downloadError ? S.msgErr : S.msgOk) }}>
          {msg}
        </div>
      )}
    </section>
  );
}

export default function ReportsPage({ role }: any) {
  const isAdmin = role === 'admin' || role === 'school_admin' || role === 'district_admin';
  const [subjects, setSubjects] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [view, setView] = useState('reports');

  useEffect(() => {
    const fetch = isAdmin ? fetchAdminSubjectsWithStudents : fetchTeacherSubjectsWithStudents;
    setLoading(true);
    fetch()
      .then((data) => setSubjects(data.subjects || []))
      .catch(() => setError('Could not load class data.'))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const classCards = useMemo(() => {
    const cards = [];
    subjects.forEach((subject) => {
      (subject.classes || []).forEach((cls) => {
        cards.push({ cls, subjectName: subject.subject_name });
      });
    });
    return cards;
  }, [subjects]);
  const transcriptStudents = useMemo(() => {
    const students = new Map();
    classCards.forEach(({ cls }) => (cls.students || []).forEach((student) => {
      students.set(String(student.student_id), student);
    }));
    return [...students.values()].sort((a, b) =>
      `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(`${b.first_name || ''} ${b.last_name || ''}`));
  }, [classCards]);

  return (
    <div style={S.page}>
      <h1 style={S.title}>Family Reports &amp; Transcripts</h1>
      <p style={S.subtitle}>
        {view === 'reports'
          ? 'Shortlist students by student, class, grade, and subject, then generate their family reports individually or together as a ZIP. These filters select students; each report includes full progress across all subjects and grades.'
          : 'Generate a standards-based student transcript and download the assessment history as a PDF.'}
      </p>
      <div role="group" aria-label="Document type" style={{ ...S.btnRow, marginBottom: 24 }}>
        {['reports', 'transcripts'].map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={view === type}
            onClick={() => setView(type)}
            style={view === type ? S.btnPrimary : S.btnSecondary}
          >
            {type === 'reports' ? 'Family Reports' : 'Transcripts'}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>Loading classes…</div>
      )}

      {!loading && error && (
        <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 12, padding: '14px 18px', color: '#b91c1c', fontWeight: 600, marginBottom: 24 }}>
          {error}
        </div>
      )}

      {!loading && !error && classCards.length === 0 && (
        <div style={{ background: '#f8fafc', border: '1px solid var(--ae-border)', borderRadius: 12, padding: '20px 24px', color: '#6b7280', fontSize: 14 }}>
          No classes found. Reports will appear here once classes are set up.
        </div>
      )}

      {!loading && classCards.length > 0 && (
        view === 'transcripts'
          ? <StudentTranscript key={role} students={transcriptStudents} />
          : <FamilyReports classCards={classCards} />
      )}
    </div>
  );
}
